'use client'

import React, { useEffect, useRef, useState } from 'react'
import { useAuth, useTranslation } from '@payloadcms/ui'
import { Bell, BellRing, X } from 'lucide-react'

type Notification = {
  id: string
  title?: string
  message?: string
  createdAt: string
  read?: boolean
}

export const NotificationsHeader: React.FC = () => {
  const { user } = useAuth()
  const [unread, setUnread] = useState<Notification[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const popupRef = useRef<HTMLDivElement>(null)

  const { i18n, t } = useTranslation()

  const fetchUnread = async () => {
    try {
      const res = await fetch(`/api/notifications?where[read][equals]=false&limit=0&depth=0`, {
        credentials: 'include',
      })
      const data = await res.json()
      setUnread(data.docs || [])
    } catch (e) {
      // console.error('Error fetching unread notifications', e)
    }
  }

  useEffect(() => {
    if (!user) return

    fetchUnread() // initial
    const id = setInterval(fetchUnread, 60000) // poll every 1min
    return () => clearInterval(id)
  }, [user])

  const handleRedirectToUnreadNotifications = () => {
    window.location.href = '/admin/collections/notifications?where[read][equals]=false'
  }

  const hasUnread = unread.length > 0

  const handleTogglePopup = () => {
    setIsOpen(!isOpen)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 60) {
      return i18n.language === 'ar'
        ? `منذ ${diffMins} دقيقة`
        : i18n.language === 'fr'
          ? `Il y a ${diffMins} min`
          : `It has been ${diffMins} minutes`
    } else if (diffHours < 24) {
      return i18n.language === 'ar'
        ? `منذ ساعة ${diffHours}`
        : i18n.language === 'fr'
          ? `Il y a ${diffHours}h`
          : `It has been ${diffHours} hours`
    } else if (diffDays < 30) {
      return i18n.language === 'ar'
        ? `منذ ${diffDays} أيام`
        : i18n.language === 'fr'
          ? `Il y a ${diffDays}j`
          : `It has been ${diffDays} days`
    }

    
    const month = date.toLocaleString('fr-FR', { month: 'short' })
    const day = date.getDate()
    const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    return `${month} ${day}, ${time}`
  }

  return (
    <div style={{ position: 'relative' }} ref={popupRef}>
      <button
        type="button"
        onClick={handleTogglePopup}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 10px',
          borderRadius: 8,
          background: 'transparent',
          border: '1px solid var(--theme-elevation-150)',
          cursor: 'pointer',
          position: 'absolute',
          top: '30px',
          right: i18n?.language === 'ar' ? undefined : '19rem',
          left: i18n?.language === 'ar' ? '19rem' : undefined,
          zIndex: 1000,
        }}
        title="Notifications"
      >
        {hasUnread ? <BellRing size={16} /> : <Bell size={16} />}
        {unread.length > 0 && (
          <span
            style={{
              minWidth: 18,
              height: 18,
              borderRadius: 999,
              fontSize: 12,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 5px',
              background: 'var(--color-error-500)',
              color: 'white',
            }}
          >
            {unread.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '80px',
            right: i18n?.language === 'ar' ? undefined : '19rem',
            left: i18n?.language === 'ar' ? '19rem' : undefined,
            width: '400px',
            maxHeight: '500px',
            background: 'var(--theme-elevation-0)',
            border: '1px solid var(--theme-elevation-150)',
            borderRadius: 12,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            zIndex: 1001,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--theme-elevation-150)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, fontFamily: 'inherit' }}>
              {t('custom:notificationTitle')}
            </h3>
            <button
              onClick={handleRedirectToUnreadNotifications}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#059669',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {t('custom:viewAll')}
            </button>
          </div>

          {/* Notifications List */}
          <div
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
            }}
          >
            {unread.length === 0 ? (
              <div
                style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: 'var(--theme-elevation-400)',
                }}
              >
                {t('custom:noNotifications')}
              </div>
            ) : (
              unread.map((notification) => (
                <div
                  key={notification.id}
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--theme-elevation-150)',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                    background: 'var(--theme-elevation-0)',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--theme-elevation-50)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--theme-elevation-0)'
                  }}
                >
                  {/* Icon */}
                  <div
                    style={{
                      width: '50px',
                      height: '50px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="22" cy="22" r="22" fill="#006633"/>
                      <g clip-path="url(#clip0_2538_5581)">
                      <path d="M30.2502 32.4995C26.5237 32.4995 23.5002 29.4761 23.5002 25.7495C23.5002 22.023 26.5237 18.9995 30.2502 18.9995C33.9768 18.9995 37.0002 22.023 37.0002 25.7495C37.0002 29.4761 33.9768 32.4995 30.2502 32.4995ZM19.5909 7.30422C20.1627 6.86828 20.9831 6.89641 21.5221 7.39797L31.7221 16.8667C31.244 16.787 30.7518 16.7448 30.2549 16.7448C26.5706 16.7448 23.4065 18.9573 22.0143 22.1261C21.7752 22.0417 21.5221 21.9948 21.2549 21.9948H19.7549C18.5127 21.9948 17.5049 23.0027 17.5049 24.2448V28.7448H21.7706C22.0565 29.5558 22.4549 30.3105 22.9471 30.9948L13.7502 30.9995C12.0956 30.9995 10.7502 29.6542 10.7502 27.9995V19.7495H10.0002C9.3815 19.7495 8.82837 19.3698 8.60337 18.798C8.37837 18.2261 8.52837 17.5698 8.97837 17.1527L19.4784 7.40266L19.5909 7.30891V7.30422ZM32.9409 22.8948C32.6081 22.6511 32.1393 22.7261 31.8956 23.0589L29.4112 26.4714L28.1596 25.2152C27.869 24.9245 27.3909 24.9245 27.1002 25.2152C26.8096 25.5058 26.8096 25.9839 27.1002 26.2745L28.9752 28.1495C29.1299 28.3042 29.3456 28.3839 29.5659 28.3698C29.7862 28.3558 29.9831 28.2386 30.1143 28.0605L33.1143 23.9355C33.3581 23.6027 33.2831 23.1339 32.9502 22.8902L32.9409 22.8948Z" fill="white"/>
                      </g>
                      <defs>
                      <clipPath id="clip0_2538_5581">
                      <rect width="30" height="30" fill="white" transform="translate(7 4)"/>
                      </clipPath>
                      </defs>
                    </svg>
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '13px',
                        color: 'var(--theme-elevation-400)',
                        marginBottom: '4px',
                      }}
                    >
                      {t('custom:newRequestMatched')}
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>
                      {notification.title || notification.message}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--theme-elevation-400)' }}>
                      {formatDate(notification.createdAt)}
                    </div>
                  </div>

                  {/* Close Button */}
                  {/* TODO Need an API endpoint to mark notification as read */}
                  {/* <button
                    onClick={(e) => {
                      e.stopPropagation()
                      // handleMarkAsRead(notification.id)
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--theme-elevation-400)',
                    }}
                  >
                    <X size={18} />
                  </button> */}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
